import React from 'react';
import { Container } from 'react-bootstrap';
import { Route, Switch, withRouter } from 'react-router';
import { useLocation } from 'react-router-dom';
import NotFound from '../../components/NotFound';
import CachedResponse from '../common/CachedResponse';
import BillbackExplorer from './billback/BillbackExplorer';
import RecordBillbackReceipt from './billback/RecordBillbackReceipt';
import RequestBillback from './billback/RequestBillback';
import CashDeal from './CashDeal';
import DeliverySetupReports from './DeliverySetupReports';
import FloorplanUpdate from './FloorplanUpdate';
import FormT from './FormT';
import MHIT from './MHIT';
import PayrollRecord from './payroll/PayrollRecord';
import PayrollExplorer from './payroll/PayrollExplorer';
import RebateExplorer from './rebate/RebateExplorer';
import RebateReport from './rebate/RebateReport';
import RecordReceipt from './rebate/RecordReceipt';
import RequestRebate from './rebate/RequestRebate';
import SOL from './Sol';
import PnL from './PnL';
import BrowseInventoryV2 from '../inventory/BrowseInventoryV2';
import RequestACRebate from './ac-rebate/RequestACRebate';
import RecordACReceipt from './ac-rebate/RecordACReceipt';
import ACRebateReport from './ac-rebate/ACRebateReport';
import ACRebateExplorer from './ac-rebate/ACRebateExplorer';
import Service from './Service';
import Dealboard from './Dealboard';
import LeadReport from './lead/LeadReport';
import SalesReport from './sales/SalesReport';
import {
  ROLE_ADMIN,
  ROLE_SALES,
  ROLE_SALES_AND_INVENTORY_MANAGER,
  ROLE_SALES_MANAGER,
  ROLE_SERVICE,
  ROLE_SUPER_ADMIN
} from '../../helpers/constants';
import { userHasAnyRole } from '../../helpers/roleHelpers';

const Reports = () => {
  const location = useLocation();

  const allowedRolesForPath = () => {
    const path = location.pathname;
    if (path.includes('billback')) {
      return [ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_SERVICE];
    }
    if (path.includes('dealboard')) {
      return [ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_SALES_MANAGER, ROLE_SALES, ROLE_SALES_AND_INVENTORY_MANAGER];
    }
    if (path.includes('/reports/lead')) {
      return [ROLE_SUPER_ADMIN, ROLE_ADMIN];
    }
    if (path.includes('/reports/sales')) {
      return [
        ROLE_SUPER_ADMIN,
        ROLE_ADMIN,
        ROLE_SALES_MANAGER,
        ROLE_SALES,
        ROLE_SALES_AND_INVENTORY_MANAGER
      ];
    }
    return [ROLE_SUPER_ADMIN, ROLE_ADMIN];
  };

  if (!userHasAnyRole(...allowedRolesForPath())) {
    return <NotFound text="You are not authorized to view this page" />;
  }

  const getActivePage = () => {
    return (
      <Switch>
        <Route exact path="/reports/form-t">
          <CachedResponse listEndpoint={'app-choices'} render={appChoices => <FormT appChoices={appChoices} />} />
        </Route>
        <Route exact path="/reports/sol">
          <CachedResponse listEndpoint={'app-choices'} render={appChoices => <SOL appChoices={appChoices} />} />
        </Route>
        {/* Rebate */}
        <Route exact path="/reports/request-rebate">
          <CachedResponse
            listEndpoint={'app-choices'}
            render={appChoices => <RequestRebate appChoices={appChoices} />}
          />
        </Route>
        <Route exact path="/reports/record-receipt">
          <RecordReceipt />
        </Route>
        <Route exact path="/reports/rebate-report">
          <RebateReport />
        </Route>
        <Route exact path="/reports/explore-rebate">
          <RebateExplorer />
        </Route>
        {/* AC Rebate */}
        <Route exact path="/reports/request-ac-rebate">
          <CachedResponse
            listEndpoint={'app-choices'}
            render={appChoices => <RequestACRebate appChoices={appChoices} />}
          />
        </Route>
        <Route exact path="/reports/record-ac-receipt">
          <RecordACReceipt />
        </Route>
        <Route exact path="/reports/ac-rebate-report">
          <ACRebateReport />
        </Route>
        <Route exact path="/reports/explore-ac-rebate">
          <ACRebateExplorer />
        </Route>

        {/* Billback */}
        <Route exact path="/reports/request-billback">
          <CachedResponse
            listEndpoint={'app-choices'}
            render={appChoices => <RequestBillback appChoices={appChoices} />}
          />
        </Route>
        <Route exact path="/reports/record-billback-receipt">
          <RecordBillbackReceipt />
        </Route>
        <Route exact path="/reports/explore-billback">
          <BillbackExplorer />
        </Route>
        <Route exact path="/reports/mhit-report">
          <CachedResponse listEndpoint={'app-choices'} render={appChoices => <MHIT appChoices={appChoices} />} />
        </Route>
        <Route exact path="/reports/delivery-setup-report">
          <CachedResponse
            listEndpoint={'app-choices'}
            render={appChoices => <DeliverySetupReports appChoices={appChoices} />}
          />
        </Route>
        <Route exact path="/reports/explore-payrolls">
          <CachedResponse
            listEndpoint={'app-choices'}
            render={appChoices => <PayrollExplorer appChoices={appChoices} />}
          />
        </Route>
        <Route exact path="/reports/record-payroll">
          <CachedResponse
            listEndpoint={'app-choices'}
            render={appChoices => <PayrollRecord appChoices={appChoices} />}
          />
        </Route>
        <Route exact path="/reports/floorplan-update">
          <CachedResponse
            listEndpoint={'app-choices'}
            render={appChoices => <FloorplanUpdate appChoices={appChoices} />}
          />
        </Route>
        <Route exact path="/reports/curtailments">
          <CachedResponse
            listEndpoint={'app-choices'}
            render={appChoices => <BrowseInventoryV2 preset="curtailments" appChoices={appChoices} />}
          />
        </Route>
        <Route exact path="/reports/cash-deal">
          <CachedResponse listEndpoint={'app-choices'} render={appChoices => <CashDeal appChoices={appChoices} />} />
        </Route>
        <Route exact path="/reports/pnl">
          <CachedResponse listEndpoint={'app-choices'} render={appChoices => <PnL appChoices={appChoices} />} />
        </Route>
        <Route exact path="/reports/dealboard">
          <CachedResponse listEndpoint={'app-choices'} render={appChoices => <Dealboard appChoices={appChoices} />} />
        </Route>
        <Route exact path="/reports/lead">
          <CachedResponse listEndpoint={'app-choices'} render={appChoices => <LeadReport appChoices={appChoices} />} />
        </Route>
        <Route exact path="/reports/sales">
          <CachedResponse
            listEndpoint={'app-choices'}
            render={appChoices => <SalesReport appChoices={appChoices} />}
          />
        </Route>
        <Route exact path="/reports/service">
          <CachedResponse listEndpoint={'app-choices'} render={appChoices => <Service appChoices={appChoices} />} />
        </Route>
        <Route path="*">
          <NotFound />
        </Route>
      </Switch>
    );
  };

  return (
    <>
      <Container fluid className="px-3 h-100">
        {getActivePage()}
      </Container>
    </>
  );
};

export default withRouter(Reports);
